import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { Flex } from '@/UI/styles';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { useResizeView } from '@/components/Recipe/FlowContext';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { LinkIcon, LinkBreakIcon } from '@/UI/Icons';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { useNodeSetAsCover } from './hooks/useNodeSetAsCover';
import { hasEditingPermissions } from './Utils';
import { NodeViewer } from './Shared/FileViewer';
import { NumberInput } from './Shared/NumberInput';
import type { MenuAction } from '@/components/Menu/Actions';
import type { NodeId } from 'web';
import type { ResizeData } from '@/types/node';

export function ResizeNode({ id, data }: { id: NodeId; data: ResizeData }) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const view = useResizeView(id);
  const { width, height, lockAspectRatio } = view.getOptions();
  const editable = hasEditingPermissions(role, data);
  const { canSetPoster, generateNodePreviewImageAndSetPoster } = useNodeSetAsCover(id, 'resize');

  const coverMenuItems = useMemo<MenuAction[]>(
    () => [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.PREVIEW_NODE.SET_AS_COVER),
        action: () => {
          void generateNodePreviewImageAndSetPoster();
        },
      },
    ],
    [generateNodePreviewImageAndSetPoster, t],
  );

  const handleWidthChange = useCallback((value: number) => view.setWidth(value), [view]);
  const handleHeightChange = useCallback((value: number) => view.setHeight(value), [view]);
  const toggleLockAspectRatio = useCallback(() => view.toggleLockAspectRatio(), [view]);
  const contextMenuItems = editable && canSetPoster ? coverMenuItems : undefined;

  return (
    <DynamicNode2 id={id} data={data} className="resize">
      <NodeViewer id={id} contextMenuItems={contextMenuItems} />

      <Flex
        sx={{
          width: '100%',
          gap: 0.5,
          mt: 2,
          justifyContent: 'start',
          pointerEvents: !editable ? 'none' : 'auto',
        }}
      >
        <NumberInput
          value={width}
          onSubmit={handleWidthChange}
          decimals={0}
          startAdornment={
            <Typography variant="body-sm-rg" sx={{ lineHeight: '1rem' }} color={color.White64_T}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.CROP.WIDTH_LETTER)}
            </Typography>
          }
          size="small"
          sx={{ width: '80px' }}
          aria-label="Width"
        />
        <NumberInput
          value={height}
          onSubmit={handleHeightChange}
          decimals={0}
          startAdornment={
            <Typography variant="body-sm-rg" sx={{ lineHeight: '1rem' }} color={color.White64_T}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.CROP.HEIGHT_LETTER)}
            </Typography>
          }
          size="small"
          sx={{ width: '80px' }}
          aria-label="Height"
        />
        <AppToggleButton
          sx={{
            width: '26px',
            height: '26px',
            p: 0.5,
          }}
          value={lockAspectRatio}
          onClick={toggleLockAspectRatio}
        >
          {lockAspectRatio ? <LinkIcon width={8} height={8} /> : <LinkBreakIcon width={8} height={8} />}
        </AppToggleButton>
      </Flex>
    </DynamicNode2>
  );
}
